/**
 * ATC source tracking — hidden line item property "_shopify_source"
 * ------------------------------------------------------------------
 * Every add-to-cart request gets the hidden line item property
 * "_shopify_source" = "<current page URL> | <location label>", e.g.
 *
 *   https://www.doonails.com/collections/bestseller | Unsere Bestseller
 *   https://www.doonails.com/products/starter-set | add from quick view
 *   https://www.doonails.com/ | add from cart drawer upsells
 *
 * Location label, first match wins (window.DnAtcSource.label):
 *   1. explicit  — a pending hint (DnAtcSource.hint, set by proxy buttons such
 *                  as the sticky bars), else the nearest ancestor carrying
 *                  data-atc-source="...", else the form's hidden input from
 *                  snippets/atc-source-property.liquid when it carries one
 *   2. context   — quick view, search drawer, cart empty upsells, cart drawer
 *                  upsells, cart drawer (fixed labels, see CONTEXTS)
 *   3. heading   — the closest preceding heading inside the enclosing section
 *                  wrapper, ignoring headings that belong to a product card (its
 *                  title) or to a tile inside the product grid (promo tiles)
 *   4. page      — "collection page: <collection title>", "product page:
 *                  <template>", "home page", "search page" ... for grids whose
 *                  heading sits in another section (the collection title lives
 *                  in the banner section). Title and template come from
 *                  themeVariables.atcSource (snippets/js-variables.liquid).
 *   5. fallback  — "unknown location" (should not occur in practice)
 *
 * Scripts bound to one product template (set builders, design choosers) label
 * with DnAtcSource.templateLabel(): "product.starter_set_pr_on_facelift".
 *
 * Coverage — no matter which script fires the request:
 *   a. <form action="/cart/add"> submits: the hidden input is written in the
 *      capture phase, before the theme's ProductForm builds its FormData
 *   b. window.fetch POSTs to /cart/add(.js): FormData, URLSearchParams, JSON
 *      ({id,...} or {items:[...]}) and urlencoded string bodies
 *   c. XMLHttpRequest POSTs to /cart/add(.js) (jQuery.ajax)
 *   For b/c without an element the label comes from the last click (within
 *   10 s), so app and landing page buttons resolve to their section heading.
 *   Requests with no recent interaction are labelled "automatic add".
 *
 * Values already present are left untouched: explicit call sites win
 * (theme.js gift automation, upsell pop-ups, set builders ...). Everything is
 * wrapped in try/catch — tracking must never block an add to cart.
 *
 * Load WITHOUT defer, before jQuery/vendor.js/theme.js and app scripts
 * (layout/theme.liquid). custom.js and cart-mandatory-guard wrap fetch too;
 * they capture this wrapper as their "original", so the chain stays intact.
 */
(function () {
  'use strict';
  if (window.DnAtcSource) return;

  var PROP_KEY = '_shopify_source';
  var PROP_NAME = 'properties[_shopify_source]';
  var PROP_NAME_ENCODED = 'properties%5B_shopify_source%5D';
  var SEPARATOR = ' | ';
  var FALLBACK_LABEL = 'unknown location';
  var AUTOMATIC_LABEL = 'automatic add';
  var INTERACTION_TTL_MS = 10000;
  var HINT_TTL_MS = 3000;
  var MAX_LABEL_LENGTH = 120;

  // Fixed labels for known surfaces. Order matters: the search drawer reuses the
  // empty-cart card classes, and the empty-cart upsells and the drawer upsells sit
  // inside the cart drawer, so the more specific surfaces are tested first.
  var CONTEXTS = [
    { label: 'add from quick view',
      selector: 'quick-buy-drawer, quick-buy-popover, product-quick, .product-quick, .main_quick_product, .quick_view_form, .quick-view, #quick-view, [data-quick-view]' },
    { label: 'add from search drawer',
      selector: 'predictive-search-drawer, predictive-search, .predictive-search, #search-drawer' },
    { label: 'add from cart empty upsells',
      selector: '.d-content-empty, .empty_drawer_bestseller, .empty_drawer_recent_view, .dn-empty-best, .empty_bestseller' },
    { label: 'add from cart drawer upsells',
      selector: 'dn-cart-upsells, .dn-ups-block, cart-drawer-recommendations, .mini-cart__recommendations, .cart-drawer-recommendations-form' },
    { label: 'add from cart drawer',
      selector: 'cart-drawer, #mini-cart' }
  ];
  var HEADING_SELECTOR = 'h1, h2, h3, h4, h5, h6, [role="heading"]';
  // Headings inside these belong to a single product (its title), not to the section
  var CARD_SELECTOR = '.product-item, product-item, .product-card, .new_product_item, .product_item_2025, .facelift-product-item, .dn-ups__card, .rcard, .dn-xsell__row';
  // Headings inside a product grid belong to a tile placed between the cards (promo tiles), not to the section
  var GRID_SELECTOR = 'product-list, .product-list__inner, .dn-kraster, .dn-brecher';
  // The theme's section wrapper. Sections may contain a bare <section> element without id or
  // heading (the collection grid does), so that element only counts when no wrapper exists.
  var SECTION_SELECTOR = '.shopify-section, [data-section-id], [data-section-type]';
  var PAGE_LABELS = {
    index: 'home page', collection: 'collection page', 'list-collections': 'collections list page',
    product: 'product page', search: 'search page', cart: 'cart page', page: 'content page',
    blog: 'blog page', article: 'blog article page', '404': '404 page', password: 'password page'
  };

  var lastInteraction = null; // { el, at }
  var pendingHint = null;     // { label, at }

  function pageUrl() { try { return String(window.location.href); } catch (e) { return ''; } }
  function clean(text) { return String(text == null ? '' : text).replace(/\s+/g, ' ').trim().slice(0, MAX_LABEL_LENGTH); }
  function isElement(el) { return !!(el && typeof el === 'object' && el.nodeType === 1); }
  function isForm(el) { return isElement(el) && String(el.tagName).toUpperCase() === 'FORM'; }
  function closest(el, selector) {
    try { return isElement(el) && el.closest ? el.closest(selector) : null; } catch (e) { return null; }
  }

  function isCartAddUrl(url) {
    try {
      var path = String(url).split('?')[0].split('#')[0];
      return /\/cart\/add(\.js)?$/.test(path);
    } catch (e) { return false; }
  }

  // ---- label resolution ------------------------------------------------------

  function takeHint() {
    if (!pendingHint) return '';
    var text = (Date.now() - pendingHint.at) <= HINT_TTL_MS ? pendingHint.label : '';
    pendingHint = null;
    return text;
  }

  function explicitLabel(el) {
    var carrier = closest(el, '[data-atc-source]');
    if (carrier) {
      var fromCarrier = clean(carrier.getAttribute('data-atc-source'));
      if (fromCarrier) return fromCarrier;
    }
    if (isForm(el)) {
      var input = el.querySelector('input[data-atc-source]');
      if (input) {
        var fromInput = clean(input.getAttribute('data-atc-source'));
        if (fromInput) return fromInput;
      }
    }
    return '';
  }

  function contextLabel(el) {
    for (var i = 0; i < CONTEXTS.length; i++) {
      if (closest(el, CONTEXTS[i].selector)) return CONTEXTS[i].label;
    }
    return '';
  }

  function precedes(a, b) {
    try { return !!(a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING); } catch (e) { return false; }
  }

  function sectionOf(el) { return closest(el, SECTION_SELECTOR) || closest(el, 'section'); }

  // Walk up from the element; on each level take the closest heading that comes before the
  // element and belongs to the section (not a product card's title, not a tile inside the
  // grid). Stop at the section wrapper: headings of other sections never apply.
  function headingLabel(el) {
    if (!isElement(el)) return '';
    var section = sectionOf(el);
    var node = el.parentElement;
    while (node && node !== document.body && node !== document.documentElement) {
      var headings = node.querySelectorAll(HEADING_SELECTOR);
      var best = null;
      for (var i = 0; i < headings.length; i++) {
        var h = headings[i];
        if (h === el || h.contains(el) || closest(h, CARD_SELECTOR) || closest(h, GRID_SELECTOR)) continue;
        if (!precedes(h, el)) continue;
        best = h; // later matches are closer to the element in document order
      }
      if (best) {
        var text = clean(best.textContent);
        if (text) return text;
      }
      if (node === section) break;
      node = node.parentElement;
    }
    return '';
  }

  // Shopify page type: the theme exposes request.page_type as themeVariables.settings.pageType;
  // the path is the fallback (locale prefix such as /de or /en-us stripped).
  function pageType() {
    try {
      var settings = window.themeVariables && window.themeVariables.settings;
      if (settings && settings.pageType) return String(settings.pageType);
    } catch (e) { /* fall through */ }
    try {
      var path = String(window.location.pathname).replace(/^\/[a-z]{2}(-[a-z]{2})?(?=\/|$)/i, '');
      if (path === '' || path === '/') return 'index';
      if (/^\/products\//.test(path) || /^\/collections\/[^\/]+\/products\//.test(path)) return 'product';
      if (/^\/collections\/?$/.test(path)) return 'list-collections';
      if (/^\/collections\//.test(path)) return 'collection';
      if (/^\/search/.test(path)) return 'search';
      if (/^\/cart/.test(path)) return 'cart';
      if (/^\/blogs\/[^\/]+\/./.test(path)) return 'article';
      if (/^\/blogs\//.test(path)) return 'blog';
      if (/^\/pages\//.test(path)) return 'page';
      if (/^\/account/.test(path)) return 'customers/account';
    } catch (e) { /* fall through */ }
    return '';
  }

  // Values exposed by snippets/js-variables.liquid: { template: "product.single-us-design", collectionTitle: "Soft Gel Press Ons" }
  function atcSourceVars() {
    try {
      var tv = window.themeVariables;
      return (tv && tv.atcSource && typeof tv.atcSource === 'object') ? tv.atcSource : {};
    } catch (e) { return {}; }
  }

  // Liquid {{ template }}: "product", "product.starter_set_pr_on_facelift", "collection.bundle" ...
  function templateName() { return clean(atcSourceVars().template); }

  function collectionTitle() {
    var title = clean(atcSourceVars().collectionTitle);
    if (title) return title;
    try { var h1 = document.querySelector('h1'); return h1 ? clean(h1.textContent) : ''; } catch (e) { return ''; }
  }

  // "collection page: Soft Gel Press Ons", "product page: product.single-us-design", "home page" ...
  // for elements without a section heading
  function pageLabel() {
    var type = pageType();
    if (!type) return '';
    var base = Object.prototype.hasOwnProperty.call(PAGE_LABELS, type) ? PAGE_LABELS[type]
      : (/^customers\//.test(type) ? 'account page' : type + ' page');
    if (type === 'collection') {
      var title = collectionTitle();
      if (title) return base + ': ' + title;
    }
    if (type === 'product') {
      var tpl = templateName();
      if (tpl) return base + ': ' + tpl;
    }
    return base;
  }

  // Label for scripts bound to one template (set builders, design choosers): the template
  // name, optionally behind a prefix — "product.starter_set_pr_on_facelift" or
  // "add from additional products bundle: product.single-us-design"
  function templateLabel(prefix) {
    var tpl = templateName() || pageLabel() || FALLBACK_LABEL;
    var pre = clean(prefix);
    return pre ? pre + ': ' + tpl : tpl;
  }

  // label(el, explicit): explicit text wins; else hint > data-atc-source > context > heading > page type
  function label(el, explicit) {
    try {
      if (explicit) return clean(explicit) || FALLBACK_LABEL;
      var hinted = takeHint();
      if (hinted) return hinted;
      return explicitLabel(el) || contextLabel(el) || headingLabel(el) || pageLabel() || FALLBACK_LABEL;
    } catch (e) {
      return FALLBACK_LABEL;
    }
  }

  function value(el, explicit) { return pageUrl() + SEPARATOR + label(el, explicit); }

  // Value for requests without an element: the pending hint, else the element of
  // the last click/submit (within 10 s), else "automatic add".
  function currentValue() {
    try {
      var hinted = takeHint();
      if (hinted) return pageUrl() + SEPARATOR + hinted;
      if (lastInteraction && (Date.now() - lastInteraction.at) <= INTERACTION_TTL_MS && isElement(lastInteraction.el)) {
        return value(lastInteraction.el);
      }
    } catch (e) { /* fall through */ }
    return pageUrl() + SEPARATOR + AUTOMATIC_LABEL;
  }

  // ---- payload stamping ------------------------------------------------------

  function hasProp(item) {
    return !!(item && item.properties && typeof item.properties === 'object' && item.properties[PROP_KEY] != null);
  }

  function stampItem(item, v) {
    if (!item || typeof item !== 'object') return item;
    if (!item.properties || typeof item.properties !== 'object') item.properties = {};
    if (item.properties[PROP_KEY] == null) item.properties[PROP_KEY] = v;
    return item;
  }

  // stamp(payload, el, explicit): payload is one item, {items:[...]} or an array of items.
  // A missing _shopify_source is filled with value(el, explicit); with neither given,
  // currentValue() is used. Returns the (mutated) payload.
  function stamp(payload, el, explicit) {
    try {
      var v = (isElement(el) || explicit) ? value(el, explicit) : currentValue();
      if (Array.isArray(payload)) {
        payload.forEach(function (item) { stampItem(item, v); });
      } else if (payload && typeof payload === 'object') {
        if (Array.isArray(payload.items)) payload.items.forEach(function (item) { stampItem(item, v); });
        else stampItem(payload, v);
      }
    } catch (e) { /* never block add to cart */ }
    return payload;
  }

  // getValue is called at most once and only when something is missing
  function injectIntoBody(body, getValue) {
    if (body == null) return body;

    if ((typeof FormData !== 'undefined' && body instanceof FormData) ||
        (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams)) {
      if (!body.has(PROP_NAME)) body.append(PROP_NAME, getValue());
      return body;
    }

    if (typeof body === 'string') {
      var parsed = null;
      try { parsed = JSON.parse(body); } catch (notJson) { parsed = null; }

      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        var v = null;
        var need = function () { if (v === null) v = getValue(); return v; };
        if (Array.isArray(parsed.items)) {
          parsed.items.forEach(function (item) {
            if (item && typeof item === 'object' && !hasProp(item)) stampItem(item, need());
          });
        } else if (!hasProp(parsed)) {
          stampItem(parsed, need());
        }
        return v === null ? body : JSON.stringify(parsed);
      }

      if (parsed === null && body.indexOf(PROP_NAME) === -1 && body.indexOf(PROP_NAME_ENCODED) === -1) {
        // application/x-www-form-urlencoded
        return body + (body.length ? '&' : '') + encodeURIComponent(PROP_NAME) + '=' + encodeURIComponent(getValue());
      }
    }

    return body;
  }

  // ---- 1. form submits (capture phase, before theme/app submit handlers) ----
  document.addEventListener('submit', function (event) {
    try {
      var form = event.target;
      if (!isForm(form) || !isCartAddUrl(form.getAttribute('action') || '')) return;
      lastInteraction = { el: form, at: Date.now() };
      var input = form.querySelector('input[name="' + PROP_NAME + '"]');
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = PROP_NAME;
        form.appendChild(input);
      }
      input.value = value(form);
    } catch (e) { /* never block add to cart */ }
  }, true);

  // ---- remember the last click: label source for fetch/XHR without a form -----
  document.addEventListener('click', function (event) {
    try { if (isElement(event.target)) lastInteraction = { el: event.target, at: Date.now() }; } catch (e) { /* ignore */ }
  }, true);

  // ---- 2. window.fetch ----------------------------------------------------------
  if (typeof window.fetch === 'function') {
    var originalFetch = window.fetch;
    window.fetch = function (input, init) {
      try {
        var url = typeof input === 'string' ? input : (input && typeof input.url === 'string' ? input.url : String(input || ''));
        var method = (init && init.method) || (input && input.method) || 'GET';
        if (isCartAddUrl(url) && String(method).toUpperCase() === 'POST' && init && init.body != null) {
          init = Object.assign({}, init, { body: injectIntoBody(init.body, currentValue) });
        }
      } catch (e) { /* fall through with the original arguments */ }
      return originalFetch.call(this, input, init);
    };
  }

  // ---- 3. XMLHttpRequest ------------------------------------------------------
  if (typeof XMLHttpRequest !== 'undefined' && XMLHttpRequest.prototype) {
    var originalOpen = XMLHttpRequest.prototype.open;
    var originalSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method, url) {
      try { this._dnAtcSourceTarget = String(method).toUpperCase() === 'POST' && isCartAddUrl(url); } catch (e) { this._dnAtcSourceTarget = false; }
      return originalOpen.apply(this, arguments);
    };

    XMLHttpRequest.prototype.send = function (body) {
      try { if (this._dnAtcSourceTarget && body != null) arguments[0] = injectIntoBody(body, currentValue); } catch (e) { /* send the original body */ }
      return originalSend.apply(this, arguments);
    };
  }

  window.DnAtcSource = {
    PROP_KEY: PROP_KEY,
    PROP_NAME: PROP_NAME,
    label: label,          // (el, explicit) -> "Unsere Bestseller"
    value: value,          // (el, explicit) -> "https://... | Unsere Bestseller"
    current: currentValue, // () -> value for the last interaction
    stamp: stamp,          // (payload, el, explicit) -> payload with _shopify_source on every item
    templateLabel: templateLabel, // (prefix?) -> "product.starter_set_pr_on_facelift" / "prefix: product.x"
    hint: function (text) { try { pendingHint = { label: clean(text), at: Date.now() }; } catch (e) { pendingHint = null; } }
  };
})();
